/**
 * Caregiver QR Code Settings Component
 * Displays QR code for caregivers to scan and enable push notifications
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, Users, Smartphone, Trash2, RefreshCw, Copy, Check, Bell, Send, Clock, ShieldCheck, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCaregiverTranslation } from '@/hooks/useCaregiverTranslation';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CaregiverToken {
  token: string;
  deviceInfo: string;
  nickname?: string;
  notificationType?: 'all' | 'messages_only';
  addedAt: string;
}

export function CaregiverQRSettings() {
  const navigate = useNavigate();
  const { t, dir } = useCaregiverTranslation();
  const { user } = useAuth();
  const [caregiverTokens, setCaregiverTokens] = useState<CaregiverToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [connectUrl, setConnectUrl] = useState('');
  const [pairingCode, setPairingCode] = useState('');
  const [isUrlLoading, setIsUrlLoading] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  
  // Always use published URL for QR codes so external devices can access them
  // Preview URLs require Lovable auth which strips query params on redirect
  const PUBLISHED_ORIGIN = 'https://homecare-lovable.lovable.app';
  
  // Fetch signed URL from edge function
  const fetchSignedUrl = async () => {
    if (!user) return;
    
    setIsUrlLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-caregiver-link', {
        body: { origin: PUBLISHED_ORIGIN }
      });
      
      if (error) throw error;
      
      if (data?.url) {
        setConnectUrl(data.url);
        if (data.code) {
          setPairingCode(data.code);
        }
        if (data.expiresAt) {
          setExpiresAt(data.expiresAt);
        }
      }
    } catch (err) {
      // Fallback to unsigned URL — use short path without UUID for iOS QR compatibility
      setConnectUrl(`${PUBLISHED_ORIGIN}/connect-caregiver/${user.id}`);
      setExpiresAt(null);
    } finally {
      setIsUrlLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id && !connectUrl) {
      fetchSignedUrl();
    }
  }, [user?.id]);

  // Check expiration every minute
  useEffect(() => {
    if (!expiresAt) return;
    
    const checkExpiry = () => {
      // Just trigger a re-render to update isExpired
      setExpiresAt(prev => prev);
    };
    
    // Check every 30 seconds
    const interval = setInterval(checkExpiry, 30 * 1000);
    
    return () => clearInterval(interval);
  }, [expiresAt]);
  
  // Format expiration time
  const formatExpirationTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  // Check if QR code is expired
  const isExpired = expiresAt ? Date.now() > expiresAt : false;
  
  // Generate QR code using qrserver.com API (since qrcode.react doesn't work)
  const qrCodeUrl = connectUrl && !isExpired
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(connectUrl)}`
    : '';

  // Fetch caregiver tokens from database
  const fetchCaregiverTokens = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('caregiver_push_tokens')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) throw error;
      
      if (data?.caregiver_push_tokens && Array.isArray(data.caregiver_push_tokens)) {
        setCaregiverTokens(data.caregiver_push_tokens as unknown as CaregiverToken[]);
      } else {
        setCaregiverTokens([]);
      }
    } catch (error) {
      console.error('Error fetching caregiver tokens:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCaregiverTokens();
  }, [user]);

  // Poll for caregiver token updates (replaces Realtime subscription for security)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      fetchCaregiverTokens();
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [user]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(connectUrl);
      setCopied(true);
      toast.success((t as any)('linkCopied') || 'הקישור הועתק!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error((t as any)('copyFailed') || 'לא הצלחנו להעתיק');
    }
  };

  const handleRemoveCaregiver = async (index: number) => {
    if (!user) return;
    
    try {
      const updatedTokens = caregiverTokens.filter((_, i) => i !== index);
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await supabase
        .from('user_settings')
        .update({ caregiver_push_tokens: updatedTokens as any })
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      setCaregiverTokens(updatedTokens);
      toast.success((t as any)('caregiverRemoved') || 'המטפל הוסר בהצלחה');
    } catch (error) {
      console.error('Error removing caregiver:', error);
      toast.error((t as any)('removeError') || 'שגיאה בהסרת המטפל');
    }
  };

  const handleClearAllCaregivers = async () => {
    if (!user || caregiverTokens.length === 0) return;
    
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await supabase
        .from('user_settings')
        .update({ caregiver_push_tokens: [] as any })
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      setCaregiverTokens([]);
      toast.success((t as any)('allCaregiversRemoved') || 'כל המכשירים הוסרו בהצלחה');
    } catch (error) {
      console.error('Error clearing caregivers:', error);
      toast.error((t as any)('clearError') || 'שגיאה בניקוי המכשירים');
    }
  };

  const handleSendTestNotification = async () => {
    if (!user || caregiverTokens.length === 0) return;
    
    setIsSendingTest(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-push-alert', {
        body: {
          patientId: user.id,
          alertType: 'test'
        }
      });
      
      if (error) throw error;
      
      if (data?.configured === false) {
        toast.info((t as any)('pushNotConfigured') || 'התראות Push עדיין לא הוגדרו במערכת');
      } else if (data?.sent > 0) {
        toast.success((t as any)('testNotificationSent') || `נשלחה התראת בדיקה ל-${data.sent} מכשירים`);
      } else if (data?.total > 0 && data?.sent === 0) {
        toast.error('שגיאה בשליחת ההתראה - ייתכן שיש בעיית הגדרות Firebase');
      } else {
        toast.info((t as any)('noCaregiversToNotify') || 'אין מטפלים מחוברים לשליחת התראה');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error((t as any)('testNotificationFailed') || 'שגיאה בשליחת התראת בדיקה');
    } finally {
      setIsSendingTest(false);
    }
  };

  // Extract device name from user agent
  const getDeviceName = (userAgent: string): string => {
    if (userAgent.includes('iPhone')) return 'iPhone';
    if (userAgent.includes('iPad')) return 'iPad';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac')) return 'Mac';
    if (userAgent.includes('Linux')) return 'Linux';
    return (t as any)('unknownDevice') || 'מכשיר לא ידוע';
  };

  if (!user) {
    return (
      <div className="p-4 bg-muted/30 rounded-xl border border-border" dir={dir}>
        <div className="flex items-center gap-3 text-muted-foreground">
          <QrCode className="w-5 h-5" />
          <p className="text-sm">
            {(t as any)('loginRequired') || 'יש להתחבר כדי לצפות בקוד QR'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" dir={dir}>
      {/* Sub-section 1: Add Contact - Settings card style */}
      <div className="bg-card rounded-2xl border border-border p-6 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 rounded-xl bg-primary/10 text-primary"><QrCode className="w-5 h-5" /></div>
          <h2 className="text-lg font-bold">{(t as any)('addContact') || 'הוספת איש קשר'}</h2>
        </div>
        
        <p className="text-sm text-muted-foreground">
          {(t as any)('userContactsDescription') || 'אנשי קשר שהמשתמש יכול לשלוח אליהם הודעות לשמירה על קשר וקריאה לעזרה בעת הצורך.'}
        </p>
        
        {/* QR Code Display */}
        <div className="flex flex-col items-center gap-4 p-6 bg-muted/30 rounded-xl">
          <p className="font-medium">
            {(t as any)('caregiverQRDesc') || 'סרקו לחיבור מכשיר טלפון'}
          </p>
          
          {/* QR Code Image */}
          <div className="bg-white p-4 rounded-xl shadow-sm min-w-[216px] min-h-[216px] flex items-center justify-center relative">
            {isUrlLoading ? (
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            ) : isExpired ? (
              <div className="flex flex-col items-center gap-3 text-center">
                <Clock className="w-12 h-12 text-destructive" />
                <p className="text-destructive font-medium text-sm">
                  {(t as any)('qrExpired') || 'קוד ה-QR פג תוקף'}
                </p>
                <Button
                  variant="default"
                  size="sm"
                  onClick={fetchSignedUrl}
                  disabled={isUrlLoading}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${isUrlLoading ? 'animate-spin' : ''}`} />
                  {(t as any)('generateNewQR') || 'צור QR חדש'}
                </Button>
              </div>
            ) : qrCodeUrl ? (
              <img 
                src={qrCodeUrl} 
                alt="QR Code for caregiver connection"
                className="w-48 h-48"
              />
            ) : (
              <QrCode className="w-12 h-12 text-muted-foreground" />
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {/* Copy Link Button - only show if not expired */}
            {!isExpired && (
              <Button
                variant="outline"
                onClick={handleCopyLink}
                disabled={isUrlLoading || !connectUrl}
                className="flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-500" />
                    {(t as any)('copied') || 'הועתק!'}
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    {(t as any)('copyLink') || 'העתק קישור'}
                  </>
                )}
              </Button>
            )}
            
            {/* Refresh QR Button - always visible */}
            <Button
              variant="default"
              onClick={fetchSignedUrl}
              disabled={isUrlLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isUrlLoading ? 'animate-spin' : ''}`} />
              {(t as any)('refreshQR') || 'רענן קוד'}
            </Button>
          </div>
          
          {/* Expiration Info - show remaining time when not expired */}
          {expiresAt && !isExpired && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span>
                {(t as any)('qrExpiresAt') || 'תוקף עד:'} {formatExpirationTime(expiresAt)}
              </span>
            </div>
          )}

          {/* Pairing Code Display */}
          {pairingCode && !isExpired && (
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">
                {(t as any)('iphoneCodeInstruction') || 'למשתמשי iPhone, הזינו את הקוד הבא לאחר ההתקנה-'}
              </p>
              <p className="text-2xl font-mono font-bold tracking-[0.3em] text-foreground select-all">
                {pairingCode}
              </p>
            </div>
          )}
          
          <p className="text-sm text-muted-foreground text-center max-w-md">
            {(t as any)('caregiverQRInstructions') || 'סריקת הקוד מאפשרת קבלת הודעות מהמשתמש כהתראות Push לטלפון האישי, גם כאשר הדפדפן סגור. ניתן להגדיר קבלת הודעות רגילות ו/או התראות קריאה לעזרה.'}
          </p>
        </div>
      </div>

      {/* Sub-section 2: Saved Contacts - Settings card style */}
      <div className="bg-card rounded-2xl border border-border p-6 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 rounded-xl bg-primary/10 text-primary"><Users className="w-5 h-5" /></div>
          <h2 className="text-lg font-bold">{(t as any)('savedContacts') || 'אנשי קשר שמורים'}</h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {caregiverTokens.length}/10
            </span>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : caregiverTokens.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <Smartphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                {(t as any)('noCaregiversConnected') || 'אין אנשי קשר מחוברים עדיין'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {caregiverTokens.map((token, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-4 h-4 text-primary" />
                  </div>
                  <p className="font-medium text-sm flex-shrink-0">
                    {token.nickname || getDeviceName(token.deviceInfo)}
                  </p>
                  {token.notificationType === 'messages_only' ? (
                    <Badge variant="secondary" className="text-xs flex items-center gap-1 flex-shrink-0">
                      <MessageSquare className="w-3 h-3" />
                      {(t as any)('messagesOnly') || 'הודעות בלבד'}
                    </Badge>
                  ) : (
                    <Badge variant="default" className="text-xs flex items-center gap-1 bg-primary/80 flex-shrink-0">
                      <ShieldCheck className="w-3 h-3" />
                      {(t as any)('allAlerts') || 'הכל'}
                    </Badge>
                  )}
                  <p className="text-xs text-muted-foreground flex-shrink-0">
                    {token.addedAt 
                      ? new Date(token.addedAt).toLocaleDateString()
                      : (t as any)('recentlyConnected') || 'חובר לאחרונה'}
                  </p>
                  <div className="flex-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/settings/caregiver/delete?index=${index}`)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          {/* Actions for connected caregivers */}
          {caregiverTokens.length > 0 && (
            <div className="flex flex-col gap-2 mt-3">
              {/* Test Notification Button */}
              <Button
                variant="outline"
                onClick={handleSendTestNotification}
                disabled={isSendingTest}
                className="w-full flex items-center justify-center gap-2"
              >
                {isSendingTest ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {(t as any)('sendTestNotification') || 'שלח התראת בדיקה'}
              </Button>
              
              {/* Clear All Button */}
              <Button
                variant="ghost"
                onClick={handleClearAllCaregivers}
                className="w-full flex items-center justify-center gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="w-4 h-4" />
                {(t as any)('clearAllCaregivers') || 'הסר את כל אנשי הקשר'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Info Box - Settings card style */}
      <div className="bg-card rounded-2xl border border-border p-6 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-border">
          <div className="p-2 rounded-xl bg-primary/10 text-primary"><Bell className="w-5 h-5" /></div>
          <h2 className="text-lg font-bold">{(t as any)('howPushAlertsWork') || 'איך זה עובד?'}</h2>
        </div>
        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
          <li>{(t as any)('pushAlertInfo1') || 'אנשי קשר סורקים את הקוד או נכנסים לקישור מהטלפון.'}</li>
          <li>{(t as any)('pushAlertInfo2') || 'מאשרים קבלת התראות בדפדפן שלהם.'}</li>
          <li>{(t as any)('pushAlertInfo3') || 'בוחרים את סוג ההתראות: הכל (כולל קריאה לעזרה) או רק הודעות רגילות לשמירה על קשר.'}</li>
          <li>{(t as any)('pushAlertInfo4') || 'מקבלים הודעות מהמשתמש בהתאם לשליחה שלו, כהתראה לטלפון האישי.'}</li>
          <li>{(t as any)('pushAlertInfo5') || 'אם מוגדרת קבלת קריאה לעזרה- מקבלים התראה מיידית כשהמשתמש לוחץ על כפתור העזרה.'}</li>
        </ul>
      </div>
    </div>
  );
}
